import { createApiClient, apiClient } from "../../api/apiClient";
import { AppDispatch } from "../../store";
import { updatePulseCheckState } from "./updatePulseCheckStateUtil";
import { handleObservationErrorWithFriendlyMessage, OBSERVATION_API_NAMES } from "../../store/utils/observationErrorHandler";

/**
 * Function to fetch observation details based on the selected record ID
 * @param observationId - ID of the observation to fetch
 * @param questions - Questions data from previous fetch
 * @param dispatch - Redux dispatch function
 * @param pulseState - Current pulse state ('new', 'in-progress', 'completed')
 */
export const fetchObservationDetails = async (
  observationId: number,
  questions: any,
  dispatch: AppDispatch,
  pulseState?: string
) => {
  try {
    console.log(`Fetching observation details for ID: ${observationId}`);
    const api = createApiClient();
    const response = await apiClient(
      api,
      `/observations/${observationId}`,
      "GET"
    );

    if (response && response.ID) {
      console.log("Successfully fetched observation details");

      const observationData = response;

      // Preserve the original images array from the API response if it exists
      const originalImages = observationData.images || [];
      console.log(
        `Found ${originalImages.length} images in the API response:`,
        originalImages
      );

      // For backward compatibility: Process SVG images and signatures
      // and add them to the legacyImages array (not replacing original images)
      const legacyImages = [];

      if (
        observationData.Miscell_Image &&
        observationData.Miscell_Image.includes("<svg")
      ) {
        legacyImages.push({
          url: `data:image/svg+xml,${encodeURIComponent(
            observationData.Miscell_Image
          )}`,
          fileName: "miscellaneous-image-1.svg",
          description:
            observationData.Miscell_Image_Desc || "Miscellaneous Image 1",
        });
      }

      if (
        observationData.Miscell_Image2 &&
        observationData.Miscell_Image2.includes("<svg")
      ) {
        legacyImages.push({
          url: `data:image/svg+xml,${encodeURIComponent(
            observationData.Miscell_Image2
          )}`,
          fileName: "miscellaneous-image-2.svg",
          description:
            observationData.Miscell_Image2_Desc || "Miscellaneous Image 2",
        });
      }

      // Add signatures if present
      if (observationData.Manager_Signature) {
        legacyImages.push({
          url: observationData.Manager_Signature,
          fileName: "manager-signature.png",
          description: "Manager Signature",
        });
      }

      if (observationData.Employee_Signature) {
        legacyImages.push({
          url: observationData.Employee_Signature,
          fileName: "employee-signature.png",
          description: "Employee Signature",
        });
      }

      // Add legacy images to the observation data in a separate property
      // This ensures we don't overwrite the original images array
      if (legacyImages.length > 0) {
        observationData.legacyImages = legacyImages;
      } else {
        observationData.legacyImages = [];
      }

      // Ensure the original images array is preserved
      if (!observationData.images) {
        observationData.images = [];
      }

      // Add additional metadata about the observation
      observationData.metadata = {
        ticketNumber: observationData.TicketNumber,
        createdBy: observationData.Created_By,
        createdDate: new Date(observationData.Created),
        modifiedBy: observationData.Modified_By,
        modifiedDate: new Date(observationData.Modified),
        period: observationData.Period,
        week: observationData.Week,
        location:
          observationData.Location_Selected || observationData.Location_Select,
      };
      // Process miscellaneous images and descriptions
      // Initialize observationImages array if it doesn't exist
      if (!observationData.observationImages) {
        observationData.observationImages = [];
      }

      // Add first miscellaneous image if exists
      if (observationData.Miscell_Image) {
        observationData.observationImages[0] = {
          imageUrl: observationData.Miscell_Image,
          fileName: observationData.Miscell_Image,
          description: observationData.Miscell_Image_Desc || "",
        };
      }

      // Add second miscellaneous image if exists
      if (observationData.Miscell_Image2) {
        observationData.observationImages[1] = {
          imageUrl: observationData.Miscell_Image2,
          fileName: observationData.Miscell_Image2,
          description: observationData.Miscell_Image2_Desc || "",
        };
      }

      // Always set miscImageDescription for text inputs, regardless of whether images exist
      observationData.miscImageDescription = {
        description1: observationData.Miscell_Image_Desc || "",
        description2: observationData.Miscell_Image2_Desc || "",
      };

      // Call updatePulseCheckState from utility
      updatePulseCheckState(observationData, questions, dispatch, pulseState);
    } else {
      console.error("No data returned from observation details API");
      const noDataError = new Error("No observation details data returned from API");
      
      handleObservationErrorWithFriendlyMessage(
        OBSERVATION_API_NAMES.FETCH_OBSERVATION_DETAILS,
        noDataError,
        true,
        {
          observationId,
          pulseState,
          reason: "Empty response data"
        }
      );
    }
  } catch (e) {
    console.error("Error fetching observation details:", e);
    
    // Show error overlay modal
    handleObservationErrorWithFriendlyMessage(
      OBSERVATION_API_NAMES.FETCH_OBSERVATION_DETAILS,
      e,
      true, // Show modal overlay
      {
        observationId,
        pulseState,
      }
    );
  }
};
